import { useMemo } from 'react';
import type { PackagingInputs } from '@/stores/useInputStore';
import type { BushingType } from '@/types/bushing';
import type { DynamicRanges } from './useParameterRanges';

export interface ValidationErrors {
  packaging: Partial<Record<keyof PackagingInputs, string>>;
  targets: Record<string, string>;
  linearTravel: Record<string, string>;

  /** Whether any blocking validation error is present. */
  hasErrors: boolean;

  /** Whether any value triggers an out-of-distribution warning. */
  hasOOD: boolean;
}

/**
 * useInputValidation
 *
 * Validates:
 * - Packaging inputs against dynamically fetched ranges
 * - Core OD Min <= Core OD Max
 * - Target values
 * - Non-negative linear travel values
 * - C1 H and axial linear-travel dependency
 */
export function useInputValidation(
  packaging: PackagingInputs,
  targets: Record<string, number | null>,
  linearTravel: Record<string, number | null>,
  sleeveThickness: number,
  bushingType: BushingType,
  dynamicRanges?: DynamicRanges,
): ValidationErrors {
  return useMemo(() => {
    const packErrors: Partial<Record<keyof PackagingInputs, string>> = {};
    const targetErrors: Record<string, string> = {};
    const linearTravelErrors: Record<string, string> = {};

    let hasErrors = false;
    let hasOOD = false;

    // ------------------------------------------------------------
    // Packaging dynamic-range validation
    // ------------------------------------------------------------

    const packagingRanges = dynamicRanges?.packaging ?? {};

    for (const [key, range] of Object.entries(packagingRanges)) {
      const packagingKey = key as keyof PackagingInputs;
      const value = packaging[packagingKey];

      if (value === null || value === undefined) {
        continue;
      }

      if (range && (value < range.min || value > range.max)) {
        packErrors[packagingKey] =
          `Range: ${range.min.toFixed(1)} – ${range.max.toFixed(1)}`;

        // Dynamic-range violations are warnings.
        hasOOD = true;
      }
    }

    // ------------------------------------------------------------
    // Core OD Min <= Core OD Max
    // ------------------------------------------------------------

    const { coreOdMin, coreOdMax } = packaging;

    if (
      coreOdMin !== null &&
      coreOdMax !== null &&
      coreOdMin > coreOdMax
    ) {
      packErrors.coreOdMin = 'Min must be ≤ Max';
      packErrors.coreOdMax = 'Max must be ≥ Min';

      hasErrors = true;
    }

    // ------------------------------------------------------------
    // Linear travel must not be negative
    // ------------------------------------------------------------

    for (const [key, value] of Object.entries(linearTravel)) {
      if (value !== null && value < 0) {
        linearTravelErrors[key] = 'Linear travel cannot be negative';
        hasErrors = true;
      }
    }

    // ------------------------------------------------------------
    // C1 H and axial linear-travel validation
    // ------------------------------------------------------------

    if (bushingType === 'C1') {
      const coreLength = packaging.coreLength;
      const sleeveLength = packaging.outerSleeveLength;
      const hValue = packaging.h;
      const axialLinearTravel = linearTravel.axial_c1;

      const hasDimensions =
        coreLength !== null &&
        sleeveLength !== null;

      /*
       * Validate only when all required values are available.
       *
       * Sleeve thickness is not nullable in the store, so it does
       * not need a null check.
       */
      if (
        hasDimensions &&
        axialLinearTravel !== null &&
        axialLinearTravel !== undefined &&
        axialLinearTravel >= 0
      ) {
        /*
         * Default H:
         *
         * H_default = (core_length - sleeve_length) / 2
         */
        const hDefault = (coreLength - sleeveLength) / 2;

        /*
         * If H has not been populated yet, use its calculated
         * default for validation.
         */
        const effectiveH = hValue ?? hDefault;

        /*
         * Equivalent Python calculation:
         *
         * effective_core_length =
         *   core_length + (H - H_default)
         */
        const effectiveCoreLength =
          coreLength + (effectiveH - hDefault);

        /*
         * Maximum allowable C1 axial linear travel:
         *
         * limit =
         *   (effective_core_length - sleeve_length) / 2
         *   - sleeve_thickness
         *   - 3.5
         */
        const maximumLinearTravel =
          (effectiveCoreLength - sleeveLength) / 2 -
          sleeveThickness -
          3.5;

        /*
         * Minimum H required for the current linear travel:
         *
         * H_min =
         *   2 * (linear_travel + sleeve_thickness + 3.5)
         *   - H_default
         */
        const minimumH =
          2 *
            (axialLinearTravel +
              sleeveThickness +
              3.5) -
          hDefault;

        // Validate entered or calculated H.
        if (effectiveH < minimumH) {
          packErrors.h =
            `H must be ≥ ${minimumH.toFixed(2)} mm `;

          hasErrors = true;
        }

        // Validate C1 axial linear travel.
        if (axialLinearTravel > maximumLinearTravel) {
          linearTravelErrors.axial_c1 =
            `Linear travel must not exceed ` +
            `${maximumLinearTravel.toFixed(2)} mm`;

          hasErrors = true;
        }
      }
    }

    // ------------------------------------------------------------
    // Target validation
    // ------------------------------------------------------------

    const targetRanges = dynamicRanges?.targets ?? {};

    for (const [parameter, value] of Object.entries(targets)) {
      const errorKey = `target_${parameter}`;

      if (value !== null && value <= 0) {
        targetErrors[errorKey] = 'Must be > 0';
        hasErrors = true;
        continue;
      }

      const range = targetRanges[parameter];

      if (
        value !== null &&
        range &&
        (value < range.min || value > range.max)
      ) {
        targetErrors[errorKey] =
          `Range: ${range.min.toFixed(1)} – ${range.max.toFixed(1)}`;

        hasOOD = true;
      }
    }

    return {
      packaging: packErrors,
      targets: targetErrors,
      linearTravel: linearTravelErrors,
      hasErrors,
      hasOOD,
    };
  }, [
    packaging,
    targets,
    linearTravel,
    sleeveThickness,
    bushingType,
    dynamicRanges,
  ]);
}